import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';

import style from './style.module.css';
import {
    setToken, setUuid, setUserPoints, setUserLevel, setUserName, setUserContData, 
    setUserChapters, setMenuLabels, setMenuOpened, setUserActivityData, setCourseView,
    usernameLabel, setPasswordLabel, createPasswordLabel, Language, 
    onboardIntroText, onboardInvalidText, onboardInvalidIntroText, setOrganization, 
    validLangs, setLanguage, languages, languageParse, enLang, setSystemLabels, 
    passwordsMatchLabel, confirmPasswordLabel, setPhishingData
  } from '../../utils/constants';

import { menuLabels, sysLabels } from '../../data/translations';
import Loader from '../../assets/images/loader.svg';

import { LoginAccount, onboardAccountCheck, changePassword } from '../../services/authApi';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { faExclamationCircle } from '@fortawesome/free-solid-svg-icons';
import { useNotification } from '../../utils/notifications';

interface OnboardingProps {
  language: string;
}

const Onboarding: React.FC<OnboardingProps> = ({language}) => {

  const navigate = useNavigate()
  const notify = useNotification()

  const {user_language, otp_key } = useParams();

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [passwordVerify, setPasswordVerify] = useState('')
  const [isLoading,setIsLoading] = useState(true);
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [userUuid, setUserUuid] = useState('')
  const [badCode,setBadCode] = useState(false)
  const [userLanguage, setUserLanguage] = useState<{ short: string } | false>(false);

  const handleSavePasswordLogin = async () => {
    setIsLoading(true)
    setIsLoggingIn(true)

    if(!password || password !== passwordVerify)
    {
      setIsLoggingIn(false)
      setIsLoading(false)

      notify(sysLabels[passwordsMatchLabel][
        ((userLanguage as unknown) as Language).short || 'EN'
      ], 3000, '#FF0000')

      return
    }

    try {

      // -- First update the password, then login:
      const changePasswordRequest = await changePassword({uuid: userUuid, password: password, otp_token: otp_key, request_type: "onboarding"})
      if(!changePasswordRequest.uuid){
        setIsLoggingIn(false)
        setIsLoading(false)
        return;
      }

      const response = await LoginAccount({username,password})
      if(response.token === 'false')
      {
          setIsLoggingIn(false)
          setIsLoading(false)
          return;
      }
      setToken(response.token)
      setUuid(response.uuid)
      setUserLevel(response.level ?? 1)
      setUserPoints(response.exp)
      setUserName(response.firstname,response.lastname)
      setMenuOpened("true")
      setUserContData(JSON.stringify(response.activity_chart))
      setUserChapters(JSON.stringify(response.progress))
      setMenuLabels(JSON.stringify(menuLabels))
      setSystemLabels(JSON.stringify(sysLabels))
      setUserActivityData(JSON.stringify(response.activity))
      setOrganization(JSON.stringify(response.organization))
      setPhishingData(JSON.stringify(response.activity_phishing))

      const settings: string[] = response.settings;
      const langSetting = settings.find(item => validLangs.includes(item));
      const newLanguage = languages(languageParse(langSetting || enLang))
      setLanguage(JSON.stringify(newLanguage))

      navigate('/')
    } catch (error) 
    {
      setIsLoggingIn(false)
      setIsLoading(false)
    }
  };


  useEffect(() => {
    if (user_language) {
      setUserLanguage({ short: user_language.toUpperCase() });
    }else {
      setUserLanguage({ short: ((language as unknown) as Language).short || 'EN' });
    }
  }, [user_language]);


  useEffect(() => {
    const doCheck = async (otp_key: string) => {
      const response = await onboardAccountCheck({otp_key});
      setIsLoading(false);
      if(!response.email || !response.uuid )
      {
        console.log('bad code')
        setBadCode(true)
        return
      }
      setBadCode(false)
      setUsername(response.email)
      setUserUuid(response.uuid)
    };

    const timer = setTimeout(() => {
      doCheck(otp_key ?? '')
    }, 1000);

    return () => clearTimeout(timer);
  }, [otp_key]);

  useEffect(() => {
    setCourseView("false")
  }, [])

  return (
    <div className={style.wrapper}>
      <div className={style.container}>
        <div className={style.loginContainer}>
          <div className={style.logo}></div>
          {
            isLoading || isLoggingIn ? 
              <div className={style.loader}>
                  <img src={Loader} />
              </div> :
              !badCode ?  
                <>
                <div className={style.onboardingIntro}>
                  { 
                      sysLabels[onboardIntroText][
                        ((userLanguage as unknown) as Language).short || 'EN'
                      ]
                    }
                </div>
                  <input
                    type="text"
                    placeholder={
                      sysLabels[usernameLabel][
                        ((userLanguage as unknown) as Language).short || 'EN'
                      ]
                    }
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    className={style.loginInputDisabled}
                    disabled
                  />
                  <input
                    type="password"
                    placeholder={
                      sysLabels[createPasswordLabel][
                        ((userLanguage as unknown) as Language).short || 'EN'
                      ]
                    }
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className={style.loginInput}
                  />
                  <input
                    type="password"
                    placeholder={
                      sysLabels[confirmPasswordLabel][
                        ((userLanguage as unknown) as Language).short || 'EN'
                      ]
                    }
                    value={passwordVerify}
                    onChange={(e) => setPasswordVerify(e.target.value)}
                    className={style.loginInput}
                  />
                  <button
                    onClick={handleSavePasswordLogin}
                    disabled={isLoading}
                    className={style.loginButton}
                  >
                    { 
                      sysLabels[setPasswordLabel][
                        ((userLanguage as unknown) as Language).short || 'EN'
                      ]
                    }
                  </button>
                </> :
                <>
                  <div className={style.badCode}>
                    <div className={style.badCodeIcon}>
                      <FontAwesomeIcon icon={faExclamationCircle} />
                    </div>
                    <div className={style.badCodeTitle}>
                      { 
                          sysLabels[onboardInvalidText][
                            ((userLanguage as unknown) as Language).short || 'EN'
                          ]
                      }
                    </div>
                    <div className={style.badCodeExplain}>
                      { 
                          sysLabels[onboardInvalidIntroText][
                            ((userLanguage as unknown) as Language).short || 'EN'
                          ]
                      }
                    </div>
                  </div>
                </>
          }   
        </div>
      </div>
    </div>
  );
};

export default Onboarding;
